import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  ArrowLeft, 
  Loader2,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Trash2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

import { SEO } from "@/components/seo/SEO";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import type { Tables } from "@/integrations/supabase/types";

type Document = Tables<"documents">;

interface ArchivedAction {
  id: string;
  type: 'action' | 'document';
  title: string;
  documentTitle?: string;
  documentId?: string;
  archivedAt: string;
  reason: 'done' | 'dismissed';
  filePath?: string;
  mimeType?: string | null;
}

export default function ActionsArchive() {
  const navigate = useNavigate();
  const { openDocument } = useDocumentPreview();
  
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ArchivedAction[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchArchivedActions();
  }, []);

  const fetchArchivedActions = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    // Fetch dismissed/done actions
    const { data: actionsData } = await supabase
      .from("document_actions")
      .select(`
        *,
        document:documents!document_id (
          id,
          title,
          file_path,
          mime_type
        )
      `)
      .eq("user_id", user.id)
      .in("status", ["done", "dismissed"])
      .order("updated_at", { ascending: false })
      .limit(100);

    // Fetch dismissed documents (expiry_dismissed_at not null)
    const { data: dismissedDocs } = await supabase
      .from("documents")
      .select("id, title, file_path, mime_type, expiry_dismissed_at, doc_type")
      .eq("user_id", user.id)
      .not("expiry_dismissed_at", "is", null)
      .order("expiry_dismissed_at", { ascending: false })
      .limit(100);

    const archivedItems: ArchivedAction[] = [];

    // Transform actions
    (actionsData || []).forEach(action => {
      archivedItems.push({
        id: action.id,
        type: 'action',
        title: (action as any).custom_title || action.label,
        documentTitle: action.document?.title,
        documentId: action.document_id || undefined,
        archivedAt: action.updated_at,
        reason: action.status === 'done' ? 'done' : 'dismissed',
        filePath: action.document?.file_path,
        mimeType: action.document?.mime_type,
      });
    });

    // Transform dismissed documents
    (dismissedDocs || []).forEach(doc => {
      archivedItems.push({
        id: `doc-${doc.id}`,
        type: 'document',
        title: `Alerte expiration`,
        documentTitle: doc.title,
        documentId: doc.id,
        archivedAt: doc.expiry_dismissed_at!,
        reason: 'dismissed', // Document alerts are always dismissed, not "done"
        filePath: doc.file_path,
        mimeType: doc.mime_type,
      });
    });

    // Sort by archived date
    archivedItems.sort((a, b) => 
      new Date(b.archivedAt).getTime() - new Date(a.archivedAt).getTime()
    );

    setItems(archivedItems);
    setLoading(false);
  };

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      item.title.toLowerCase().includes(query) ||
      item.documentTitle?.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  const handleView = async (item: ArchivedAction) => {
    if (!item.documentId) return;
    
    // Fetch complete document from database for full metadata
    const { data: doc } = await supabase
      .from("documents")
      .select("*")
      .eq("id", item.documentId)
      .single();
    
    if (doc) {
      openDocument(doc);
    }
  };

  const handleRestore = async (item: ArchivedAction) => {
    if (item.type === 'action') {
      const { error } = await supabase
        .from("document_actions")
        .update({ status: "pending" })
        .eq("id", item.id);

      if (error) {
        toast.error("Erreur lors de la restauration");
      } else {
        toast.success("Action restaurée");
        setItems(prev => prev.filter(i => i.id !== item.id));
      }
    } else {
      const docId = item.id.replace('doc-', '');
      const { error } = await supabase
        .from("documents")
        .update({ expiry_dismissed_at: null })
        .eq("id", docId);

      if (error) {
        toast.error("Erreur lors de la restauration");
      } else {
        toast.success("Alerte restaurée");
        setItems(prev => prev.filter(i => i.id !== item.id));
      }
    }
  };

  const handleDelete = async (item: ArchivedAction) => {
    if (item.type !== 'action') {
      toast.info("Les alertes de documents ne peuvent pas être supprimées définitivement");
      return;
    }

    setDeletingId(item.id);
    
    const { error } = await supabase
      .from("document_actions")
      .delete()
      .eq("id", item.id);

    if (error) {
      toast.error("Erreur lors de la suppression");
    } else {
      toast.success("Action supprimée définitivement");
      setItems(prev => prev.filter(i => i.id !== item.id));
    }
    
    setDeletingId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-6 md:py-8">
      <SEO 
        title="Actions archivées - EasyFinder"
        description="Historique des actions traitées et ignorées"
        noIndex={true}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/actions')}
            className="text-muted-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Actions archivées</h1>
            <p className="text-xs text-muted-foreground">
              {items.length} action{items.length !== 1 ? 's' : ''} archivée{items.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher dans les archives..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* List */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground">Aucune action archivée</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Les actions traitées ou ignorées apparaîtront ici
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                layout
              >
                <Card className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                        item.reason === 'done' 
                          ? "bg-success/10" 
                          : "bg-muted"
                      )}>
                        {item.reason === 'done' ? (
                          <CheckCircle className="w-5 h-5 text-success" />
                        ) : (
                          <XCircle className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-foreground truncate">
                              {item.title}
                            </p>
                            {item.documentTitle && (
                              <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {item.documentTitle}
                              </p>
                            )}
                          </div>
                          
                          <Badge 
                            variant="outline" 
                            className={cn(
                              "text-[10px] shrink-0",
                              item.reason === 'done' 
                                ? "bg-success/10 text-success border-success/20" 
                                : "bg-muted text-muted-foreground"
                            )}
                          >
                            {item.reason === 'done' ? 'Traité' : 'Ignoré'}
                          </Badge>
                        </div>

                        <p className="text-xs text-muted-foreground mt-2">
                          Archivé le {format(new Date(item.archivedAt), "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                        </p>

                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-3">
                          {item.documentId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => handleView(item)}
                            >
                              Voir le document
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => handleRestore(item)}
                          >
                            Restaurer
                          </Button>
                          {item.type === 'action' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-destructive hover:text-destructive"
                              onClick={() => handleDelete(item)}
                              disabled={deletingId === item.id}
                            >
                              {deletingId === item.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
